/**
 * Async job-lifecycle slice.
 *
 * Owns the add/complete/remove actions for the three pending-job families
 * (Kling video, Banan image, Higgsfield Marketing Studio video). The complete*
 * actions delegate to the pure job-completion mappers in ../jobCompletion.
 */

import type { StoreApi } from "zustand";
import type { AdsWorkspaceState, SubAgentId } from "../useAdsWorkspaceStore.types";
import { requestCompanionDockAutoOpen } from "../adsCompanionEvents";
import {
  applyBananJobCompletion,
  applyHiggsfieldJobCompletion,
  applyKlingJobCompletion,
} from "../jobCompletion";

type SetState = StoreApi<AdsWorkspaceState>["setState"];
type GetState = StoreApi<AdsWorkspaceState>["getState"];

export type JobsActions = Pick<
  AdsWorkspaceState,
  | "addPendingKlingJob"
  | "completeKlingJob"
  | "removeKlingJob"
  | "addPendingBananJob"
  | "completeBananJob"
  | "removeBananJob"
  | "addPendingHiggsfieldJob"
  | "completeHiggsfieldJob"
  | "removeHiggsfieldJob"
>;

export function createJobsSlice(set: SetState, _get: GetState): JobsActions {
  return {
    addPendingKlingJob: (job) =>
      set((state) => {
        if (state.pendingKlingJobs.some((j) => j.jobId === job.jobId)) return state;
        requestCompanionDockAutoOpen("agent-dispatch");
        const agentId: SubAgentId = job.provider === "heygen" ? "agent_v" : "vid";
        return {
          pendingKlingJobs: [...state.pendingKlingJobs, job],
          revealedSubAgents: state.revealedSubAgents.includes(agentId)
            ? state.revealedSubAgents
            : [...state.revealedSubAgents, agentId],
        };
      }),

    completeKlingJob: (jobId, videoUrl, thumbnailUrl, videos) =>
      set((state) => applyKlingJobCompletion(state, jobId, videoUrl, thumbnailUrl, videos) ?? state),

    removeKlingJob: (jobId) =>
      set((state) => ({
        pendingKlingJobs: state.pendingKlingJobs.filter((j) => j.jobId !== jobId),
      })),

    addPendingBananJob: (job) =>
      set((state) => {
        if (state.pendingBananJobs.some((j) => j.jobId === job.jobId)) return state;
        requestCompanionDockAutoOpen("agent-dispatch");
        return {
          pendingBananJobs: [...state.pendingBananJobs, job],
          revealedSubAgents: state.revealedSubAgents.includes("banan")
            ? state.revealedSubAgents
            : [...state.revealedSubAgents, "banan"],
        };
      }),

    completeBananJob: (jobId, imageUrl, imageUrls) =>
      set((state) => applyBananJobCompletion(state, jobId, imageUrl, imageUrls) ?? state),

    removeBananJob: (jobId) =>
      set((state) => ({
        pendingBananJobs: state.pendingBananJobs.filter((j) => j.jobId !== jobId),
      })),

    addPendingHiggsfieldJob: (job) =>
      set((state) => {
        if (state.pendingHiggsfieldJobs.some((j) => j.jobId === job.jobId)) return state;
        requestCompanionDockAutoOpen("agent-dispatch");
        const agentId: SubAgentId =
          job.generator === "higgsfield_cli" ? "higgsfield_cli" : "higgsfield";
        return {
          pendingHiggsfieldJobs: [...state.pendingHiggsfieldJobs, job],
          revealedSubAgents: state.revealedSubAgents.includes(agentId)
            ? state.revealedSubAgents
            : [...state.revealedSubAgents, agentId],
        };
      }),

    completeHiggsfieldJob: (jobId, videoUrl, thumbnailUrl, videos) =>
      set(
        (state) =>
          applyHiggsfieldJobCompletion(state, jobId, videoUrl, thumbnailUrl, videos) ?? state,
      ),

    removeHiggsfieldJob: (jobId) =>
      set((state) => ({
        pendingHiggsfieldJobs: state.pendingHiggsfieldJobs.filter((j) => j.jobId !== jobId),
      })),
  };
}
